// Shared search matching for the manager-edit pickers (customer, employee,
// description).
//
// Why this exists: the pickers used to rely on MUI's default Autocomplete
// filter, which does a single substring test against the whole rendered
// label. That breaks in the ways managers actually type at a location with
// 1k+ customers:
//
//   - "gomez maria"   reversed order never matches "Maria Gomez"
//   - "maria  gomez"  a stray double space (or a trailing space baked into
//                     firstName) kills the whole-label match
//   - "maria gomez"   misses when the record is accented ("Maria Gomez" with
//                     accents on the a/o)
//   - a phone number  not part of the label at all
//
// Transaction.jsx solves part of this with a per-field filter (firstName OR
// lastName contains the input), but that one fails on any multi-word query
// because neither single field contains "maria gomez".
//
// So: normalize both sides (lowercase, strip accents, collapse runs of
// whitespace) and require EVERY whitespace-separated token in the query to
// appear somewhere in the haystack. Order-independent, punctuation-tolerant,
// and it lets the haystack carry extra searchable text (phone) that the
// visible label doesn't show.

// Combining diacritical marks occupy U+0300..U+036F. After NFD decomposition
// an accented letter becomes base letter + mark, so dropping that range
// leaves the plain ASCII letter behind. Done by code point rather than a
// regex literal so the source file stays pure ASCII.
const COMBINING_START = 0x300;
const COMBINING_END = 0x36f;

const stripAccents = (text) => {
  let out = '';
  for (let i = 0; i < text.length; i += 1) {
    const code = text.charCodeAt(i);
    if (code < COMBINING_START || code > COMBINING_END) out += text[i];
  }
  return out;
};

// "Maria  Gomez " (accented) -> "maria gomez"
export const normalizeSearch = (value) =>
  stripAccents((value == null ? '' : String(value)).normalize('NFD'))
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

// Digits only, so "(325) 666-5117" is findable by typing "3256665117".
const digitsOf = (value) =>
  (value == null ? '' : String(value)).replace(/\D/g, '');

/**
 * True when every token of `query` appears in `haystack`.
 * An all-digit token also matches against the haystack's digits, so a
 * formatted phone number is searchable as a bare number.
 */
export const matchesTokens = (haystack, query) => {
  const needle = normalizeSearch(query);
  if (!needle) return true;

  const hay = normalizeSearch(haystack);
  const tokens = needle.split(' ');
  const hayDigits = digitsOf(hay);

  return tokens.every((token) => {
    if (hay.includes(token)) return true;
    const tokenDigits = digitsOf(token);
    return Boolean(tokenDigits) && hayDigits.includes(tokenDigits);
  });
};

/**
 * Build a MUI Autocomplete `filterOptions` handler.
 *
 * @param {(option:any) => string} getHaystack  searchable text for an option
 * @param {number} [limit]  cap on rendered matches - locations carry 1k+
 *   customers and rendering them all is what makes the mobile dropdown crawl.
 *   Pass 0 for no cap.
 */
export const makeTokenFilter =
  (getHaystack, limit = 50) =>
  (options, { inputValue }) => {
    const matched = (options || []).filter((option) =>
      matchesTokens(getHaystack(option), inputValue)
    );
    return limit > 0 ? matched.slice(0, limit) : matched;
  };

// Searchable text builders - keep every picker looking at the same fields.
export const customerHaystack = (c) =>
  `${c?.firstName || ''} ${c?.lastName || ''} ${c?.phone || ''}`;

export const employeeHaystack = (e) =>
  `${e?.firstname || ''} ${e?.lastname || ''} ${e?.phone || ''}`;
